// ============================================================================
// Overall Control — where an order, PO or shipment got stuck, and who owns the delay.
//
// Brief: docs/overall-control/BRIEF.md. Orders tab spec: docs/overall-control/ORDERS_TAB_SPEC.md.
// Decisions: docs/overall-control/QUESTIONS_AND_DECISIONS.md.
//
// All three tabs run on real data (md_backend overall_control; components/orders/, pos/,
// shipments/). Anything whose rule is not defined yet shows "–" (O15). Date and filters are shared
// by all tabs.
// A date / range bar drives every tab (O13, O18). This folder is self-contained: nothing outside
// it is changed apart from the route and nav registration.
// ============================================================================
import { ApartmentOutlined } from "@ant-design/icons";
import { message, Skeleton, Tabs } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getBranches, getSlaSettings, searchOverallControl } from "./api/orders";
import { FILTER_DEBOUNCE_MS } from "./config/rules";
import DateBar, { OcRange, rangeLabel, todayRange } from "./components/date_bar";
import GlobalBar from "./components/global_bar";
import OrderPathDrawer, { OrderPathRequest } from "./components/orders/order_path_drawer";
import OrdersListDrawer, { OrdersListRequest } from "./components/orders/orders_list_drawer";
import OrdersTab from "./components/orders/orders_tab";
import PosTab from "./components/pos/pos_tab";
import ShipmentsTab from "./components/shipments/shipments_tab";
import SlaSettingsTab from "./components/sla/sla_settings";
import { OcCarrier, OcDeliveryType, OcTab } from "./types";
import { OcBranchOption, OcRow, OcScopeParams } from "./types/orders";
import { SlaSettings } from "./types/sla";
import styles from "./index.module.scss";

const TABS: { key: OcTab; label: string; audience: string }[] = [
  { key: "orders", label: "Orders", audience: "Overall · orders by lead ID" },
  { key: "pos", label: "POs", audience: "PO team" },
  { key: "shipments", label: "Shipments", audience: "Delivery team" },
  { key: "sla", label: "SLA settings", audience: "Admins only" }, // shown only to admins
];

const isTab = (v: string | null): v is OcTab => TABS.some((t) => t.key === v);

const OverallControl: React.FC = () => {
  // The open tab lives in the URL (?tab=shipments), so a refresh or a shared link lands on it.
  const [params, setParams] = useSearchParams();
  const urlTab = params.get("tab");
  // SLA settings: admins only. The backend decides (403 for everyone else) and the tab stays hidden.
  const [slaAllowed, setSlaAllowed] = useState<boolean | null>(null);
  // The admin check's payload, handed to the SLA tab the first time it opens (then dropped, so a
  // later visit refetches and shows any save made since).
  const [slaInitial, setSlaInitial] = useState<SlaSettings | null>(null);
  useEffect(() => {
    getSlaSettings()
      .then((d) => {
        setSlaInitial(d);
        setSlaAllowed(true);
      })
      .catch(() => setSlaAllowed(false));
  }, []);
  const wanted: OcTab = isTab(urlTab) ? urlTab : "orders";
  const tab: OcTab = wanted === "sla" && slaAllowed === false ? "orders" : wanted;
  const setTab = useCallback(
    (next: OcTab) => {
      const p = new URLSearchParams(params);
      p.set("tab", next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );
  useEffect(() => {
    if (tab === "sla" && slaAllowed) setSlaInitial(null); // the SLA tab has mounted with it
  }, [tab, slaAllowed]);
  const [range, setRange] = useState<OcRange>(todayRange);
  const [cities, setCities] = useState<string[]>([]);
  const [types, setTypes] = useState<OcDeliveryType[]>([]);
  const [carriers, setCarriers] = useState<OcCarrier[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [branchOptions, setBranchOptions] = useState<OcBranchOption[]>([]);
  useEffect(() => {
    getBranches()
      .then((opts) => setBranchOptions(opts ?? []))
      .catch(() => message.error("Could not load the EC list"));
  }, []);
  // The filters reach the tabs FILTER_DEBOUNCE_MS after the last pick, so ticking three cities
  // makes one request, not three. The selects themselves update at once.
  const [filters, setFilters] = useState({ cities, types, carriers, branches });
  useEffect(() => {
    const t = setTimeout(() => setFilters({ cities, types, carriers, branches }), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [cities, types, carriers, branches]);
  const [searching, setSearching] = useState(false);
  const [searchDrawer, setSearchDrawer] = useState<OrdersListRequest | null>(null);
  const [pathRequest, setPathRequest] = useState<OrderPathRequest | null>(null);
  const [status, setStatus] = useState<{ loading: boolean; updatedAt: Date | null; refresh: () => void }>({
    loading: false,
    updatedAt: null,
    refresh: () => undefined,
  });

  const scope: OcScopeParams = useMemo(
    () => ({
      date_from: range[0].format("YYYY-MM-DD"),
      date_to: range[1].format("YYYY-MM-DD"),
      cities: filters.cities,
      delivery_types: filters.types,
      carriers: tab === "pos" ? [] : filters.carriers,
      branches: filters.branches,
    }),
    [range, filters, tab],
  );

  // Every order opens its step-by-step path here, on this page (search, drill-down rows).
  const openPath = useCallback(
    (r: OcRow) =>
      r.sales_order_id &&
      setPathRequest({
        salesOrderId: r.sales_order_id,
        allocationIds: r.allocation_ids?.length ? r.allocation_ids : r.allocation_id ? [r.allocation_id] : [],
        title: r.order_id,
      }),
    [],
  );

  // An exact ENQ / MD / shipment match opens its path; anything else lists the matches.
  const search = useCallback(async (q: string) => {
    setSearching(true);
    try {
      const res = await searchOverallControl(q);
      if (res.exact?.sales_order_id) openPath(res.exact);
      else setSearchDrawer({ mode: "rows", title: `Search: “${q}”`, rows: res.matches });
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? e?.message ?? "Search failed");
    } finally {
      setSearching(false);
    }
  }, [openPath]);

  const audience = TABS.find((t) => t.key === tab)?.audience;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ApartmentOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Overall Control</h2>
      </div>

      <GlobalBar
        tab={tab}
        cities={cities}
        types={types}
        carriers={carriers}
        branches={branches}
        branchOptions={branchOptions}
        onCities={setCities}
        onTypes={setTypes}
        onCarriers={setCarriers}
        onBranches={setBranches}
        onSearch={search}
        searching={searching}
        updatedAt={status.updatedAt}
        loading={status.loading}
        onRefresh={status.refresh}
        isMock={false}
      />

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as OcTab)}
        items={TABS.filter((t) => t.key !== "sla" || slaAllowed).map((t) => ({ key: t.key, label: t.label }))}
        tabBarExtraContent={
          <div className={styles.tabExtra}>
            <span className={styles.muted}>{audience}</span>
            {tab === "sla" ? null : <DateBar value={range} onChange={setRange} />}
          </div>
        }
      />

      {tab === "orders" ? (
        <OrdersTab scope={scope} rangeLabel={rangeLabel(range)} onStatus={setStatus} onOpenPath={openPath} />
      ) : tab === "pos" ? (
        <PosTab scope={scope} rangeLabel={rangeLabel(range)} onStatus={setStatus} onOpenPath={openPath} />
      ) : tab === "shipments" ? (
        <ShipmentsTab scope={scope} rangeLabel={rangeLabel(range)} onStatus={setStatus} onOpenPath={openPath} />
      ) : slaAllowed ? (
        <SlaSettingsTab initial={slaInitial} />
      ) : (
        <Skeleton active />
      )}

      <OrdersListDrawer request={searchDrawer} onClose={() => setSearchDrawer(null)} onOpenPath={openPath} />
      <OrderPathDrawer request={pathRequest} onClose={() => setPathRequest(null)} />
    </div>
  );
};

export default OverallControl;
